import { WORLDS } from '../campaign';
import type { MilestoneDef } from '../../game/meta';

/* ═══════════════════════════════════════════════════════════════════════════
   Les jalons de maîtrise — la seule source de POINTS DE COMPÉTENCE.

   Règle de composition, et c'est elle qui tient toute l'économie debout :
   un jalon décrit un EXPLOIT, pas un total. « Terminer le monde 2 » se raconte,
   « avoir détruit 10 000 ennemis » se subit. Le premier se décroche par un
   progrès de joueur, le second par du temps de présence — et un point gagné par
   du temps de présence est un crédit, pas un point de compétence.

   La barrière est dans le type (`RunRecord` n'expose ni durée ni cumul), mais
   la barrière ne dit pas ce qui est BIEN : elle empêche seulement le pire. Le
   critère de relecture pour tout jalon ajouté ici est :

       « un joueur qui joue deux fois plus longtemps le décroche-t-il
         mécaniquement deux fois plus vite ? »

   Si oui, il n'a rien à faire dans ce fichier.

   Barème des points, calé sur la difficulté réelle :
     1 pt  — franchi en jouant correctement une fois
     2 pts — demande une intention (une contrainte qu'on s'impose)
     3 pts — demande une exécution propre du début à la fin

   Total distribué (3 mondes) : 22 points. L'arbre STAT complet en coûte 17. La
   marge de cinq points est délibérée : elle évite que le dernier palier de
   l'arbre soit l'otage du jalon le plus difficile du jeu. Un joueur qui bute sur
   le no-hit doit quand même pouvoir compléter sa branche.

   ⚠️ Les jalons de FRANCHISSEMENT sont GÉNÉRÉS depuis `WORLDS` : ajouter un monde
   ajoute automatiquement son jalon. Le barème est BORNÉ (1,2,3 pour les trois
   premiers — ils enseignent — puis 1 point chacun) pour que l'économie ne
   déborde pas : `tests/game/meta.test.ts` impose `points totaux ≤ 2× coût de
   l'arbre`. À dix mondes : franchissement 13 + maîtrise 16 = 29 ≤ 34. Au-delà,
   c'est une décision de conception (agrandir l'arbre, ou sortir des points), pas
   un ajustement mécanique — et le test le signalera bruyamment.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Métadonnées ÉDITORIALES des premiers jalons de franchissement, préservées à
 *  l'identique. Au-delà des mondes nommés ici, un libellé thématique dérivé du
 *  nom du monde et un point suffisent. */
const FRANCHISE_META: Record<string, { name: string; text: string; points: number }> = {
  'monde-1': { name: 'Première percée', text: 'Terminer le monde 1.', points: 1 },
  'monde-2': { name: 'Traversée du champ', text: 'Terminer le monde 2.', points: 2 },
  'monde-3': { name: 'Cœur du secteur', text: 'Terminer le monde 3.', points: 3 },
};

/* ── Progression : les premiers points, ceux qui apprennent au joueur que le
   système existe. Ils tombent sans rien demander de spécial — un système de
   progression invisible pendant trois heures est un système que personne
   n'utilise. GÉNÉRÉS depuis `WORLDS` pour suivre l'extension de la campagne. ── */
const franchise: MilestoneDef[] = WORLDS.map((w, i) => {
  const meta = FRANCHISE_META[w.id];
  return {
    id: `${w.id}-franchi`,
    name: meta?.name ?? `${w.name} franchi`,
    text: meta?.text ?? `Terminer ${w.name}.`,
    // Barème borné : au-delà des mondes édités à la main, 1 point — sinon
    // l'économie de points déborde (voir l'en-tête).
    points: meta?.points ?? 1,
    cond: { minWorldsCleared: i + 1 },
  };
});

const milestones: MilestoneDef[] = [
  ...franchise,

  /* ── Propreté d'exécution. Le cœur du système : ces jalons-là ne s'obtiennent
     pas en rejouant, ils s'obtiennent en jouant MIEUX. ── */
  {
    id: 'run-sans-mort',
    name: 'Coque intacte',
    text: 'Finir une run complète sans perdre une seule vie.',
    points: 3,
    cond: { cleared: true, maxDeaths: 0 },
  },
  {
    id: 'boss-sans-bombe',
    name: 'Duel à mains nues',
    text: 'Vaincre un boss au cours d’une run où aucune bombe n’a été lâchée.',
    points: 2,
    // La bombe est le bouton « annuler » du jeu : s'en priver oblige à lire les
    // patterns au lieu de les effacer. On lit `maxBombsUsed` sur la RUN entière
    // et pas sur le combat : compter par boss demanderait un compteur remis à
    // zéro à chaque phase, donc un état que la simulation n'a aucune raison de
    // tenir. La version « run entière » est plus stricte, jamais plus laxiste.
    cond: { minBossesKilled: 1, maxBombsUsed: 0 },
  },
  {
    id: 'boss-sans-degat',
    name: 'Intouchable',
    text: 'Vaincre un boss sans avoir encaissé le moindre coup de la run.',
    points: 3,
    cond: { minBossesKilled: 1, maxHitsTaken: 0 },
  },

  /* ── Densité et prise de risque. Ils mesurent une façon de jouer, pas une
     quantité de jeu : les seuils sont atteignables en UNE run et ne progressent
     pas d'une partie à l'autre. ── */
  {
    id: 'chasseur-400',
    name: 'Récolte dense',
    text: 'Détruire 400 ennemis dans une seule run.',
    points: 1,
    // ⚠️ « dans une seule run » n'est pas une formulation cosmétique : la
    // version cumulée du même jalon serait un sablier déguisé. Le seuil est
    // calé au-dessus de ce qu'une run passive produit — il faut tenir la
    // pression au lieu de laisser passer les vagues de fond.
    cond: { minKills: 400 },
  },
  {
    id: 'frolement-200',
    name: 'Sang-froid',
    text: 'Frôler 200 projectiles dans une seule run.',
    points: 2,
    cond: { minGraze: 200 },
  },
  {
    id: 'austerite',
    name: 'Austérité',
    text: 'Terminer un monde sans rien acheter à la boutique de bord.',
    points: 2,
    // Le pendant des modificateurs volontaires du plan : une contrainte que le
    // joueur s'impose, qui ne touche à aucun réglage d'équilibrage.
    cond: { minWorldsCleared: 1, maxCellsSpent: 0 },
  },
  {
    id: 'veteran',
    name: 'Vétéran',
    text: 'Finir le jeu en difficulté Vétéran.',
    points: 3,
    cond: { cleared: true, minDifficulty: 2 },
  },
];

export default milestones;
